import { createContext, useContext, useMemo } from 'react';
import { User } from '@/types/user';
import { PortalAccessLevel } from '@/types/customer';
import { useGetUser } from '@/services/api/users/queries';
import { useGetCustomers } from '@/services/api/customers/queries';
import { useAuth } from './authProvider';

export const UserContext = createContext<{
  user: User | undefined;
  isCustomerUser: boolean;
  isInternalUser: boolean;
  hasNoPortalAccess: boolean;
  portalAccess: PortalAccessLevel;
  isSubscriber: boolean;
  customerId: string | undefined;
}>({
  user: undefined,
  isCustomerUser: false,
  isInternalUser: false,
  hasNoPortalAccess: false,
  portalAccess: 'none',
  isSubscriber: false,
  customerId: undefined,
});

export const UserProvider = (props: { children?: React.ReactNode }) => {
  const { children } = props;
  const { authUser } = useAuth();
  const { data: user } = useGetUser(authUser?.user_id);
  const { data: customers } = useGetCustomers();

  // Routing keys off role (group membership), not user_type. A user is
  // either a recognized customer-portal user, a recognized staff user, or
  // unclassified — in which case they get a generic "contact Customer
  // Service" landing page rather than any portal.
  const isCustomerUser = useMemo(() => {
    if (!user) return false;
    if (user.is_superuser) return false;
    return user.groups?.includes('Customer Portal Users') ?? false;
  }, [user]);

  const isInternalUser = useMemo(() => {
    if (!user) return false;
    if (user.is_superuser) return true;
    const staffRoles = ['Admins', 'Service 1 Users', 'Service 2 Users', 'API Users', 'Marketing Users'];
    return user.groups?.some((g) => staffRoles.includes(g)) ?? false;
  }, [user]);

  const hasNoPortalAccess = useMemo(() => {
    if (!user) return false;
    return !isCustomerUser && !isInternalUser;
  }, [user, isCustomerUser, isInternalUser]);

  // For customer users, get the first customer they have access to
  const customer = useMemo(() => {
    if (!isCustomerUser || !customers?.length) return undefined;
    return customers[0];
  }, [isCustomerUser, customers]);

  const portalAccess: PortalAccessLevel = useMemo(() => {
    if (!isCustomerUser) return 'none';
    return customer?.portal_access ?? 'free';
  }, [isCustomerUser, customer?.portal_access]);

  const isSubscriber = useMemo(() => {
    return portalAccess === 'subscriber';
  }, [portalAccess]);

  return (
    <UserContext.Provider
      value={{
        user,
        isCustomerUser,
        isInternalUser,
        hasNoPortalAccess,
        portalAccess,
        isSubscriber,
        customerId: customer?.id,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('User context is not provided');
  }
  return context;
};

export const useIsCustomerUser = () => {
  const { isCustomerUser } = useUser();
  return isCustomerUser;
};

export const useIsInternalUser = () => {
  const { isInternalUser } = useUser();
  return isInternalUser;
};

export const usePortalAccess = () => {
  const { portalAccess, isSubscriber, customerId } = useUser();
  return { portalAccess, isSubscriber, customerId };
};
